/**
 * Format user name for display
 * @param {string} name - User name
 * @returns {string} - Formatted name
 */
export const formatUserName = (name) => {
  if (!name) return 'User';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

/**
 * Get user initials for avatar
 * @param {string} name - User name
 * @returns {string} - User initials
 */
export const getUserInitials = (name) => {
  if (!name) return 'U';
  return name.charAt(0).toUpperCase();
};

/**
 * Format age with proper text
 * @param {number} age - User age
 * @returns {string} - Formatted age text
 */
export const formatAge = (age) => {
  if (!age) return 'Unknown age';
  return `${age} years`;
};

/**
 * Format gender with proper text
 * @param {string} gender - User gender
 * @returns {string} - Formatted gender text
 */
export const formatGender = (gender) => {
  if (!gender) return 'Unknown';
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
};

/**
 * Generate random progress value for simulation
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} - Random progress value
 */
export const generateRandomProgress = (min = 0, max = 100) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Simulate API delay
 * @param {number} ms - Delay in milliseconds
 * @returns {Promise} - Promise that resolves after delay
 */
export const delay = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Validate user data
 * @param {Object} userData - User data object
 * @returns {boolean} - Whether data is valid
 */
export const validateUserData = (userData) => {
  if (!userData) return false;

  // Check for required fields (name and age are required, gender is optional)
  const hasRequiredFields = !!(userData.name && userData.age);

  // Additional validation for patient data from current_patient API
  const hasPatientFields = !!(userData.patientId && userData.sessionId && userData.requestId);

  // Return true if we have basic required fields (name and age)
  // Gender is optional as it may not be available in all user profiles
  return hasRequiredFields;
};

/**
 * Get current time formatted
 * @returns {string} - Current time in HH:MM format
 */
export const getCurrentTime = () => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Function to convert Western numerals to Arabic numerals
export const convertToArabicNumerals = (number) => {
  const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return number
    .toString()
    .replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};

/**
 * Check if current time is within available pod schedule slots
 * @param {Object} scheduleData - Schedule data from API
 * @param {string} scheduleData.timezone - Timezone (e.g., "Africa/Cairo")
 * @param {Array} scheduleData.entries - Array of schedule entries
 * @returns {boolean} - Whether consultation is currently available
 */
export const isPodAvailable = (scheduleData) => {
  if (!scheduleData || !scheduleData.entries || scheduleData.entries.length === 0) {
    return false;
  }

  try {
    // Get current date/time in the schedule's timezone
    const now = new Date();
    const timezone = scheduleData.timezone || 'UTC';

    // Get day of week in the schedule's timezone
    const dayFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'long'
    });
    const currentDayOfWeek = dayFormatter.format(now).toUpperCase();

    // Get current time in the schedule's timezone
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const timeParts = timeFormatter.formatToParts(now);
    const hour = timeParts.find(p => p.type === 'hour')?.value.padStart(2, '0') || '00';
    const minute = timeParts.find(p => p.type === 'minute')?.value.padStart(2, '0') || '00';
    const currentTime = `${hour}:${minute}`;

    // Check if current day and time match any schedule entry
    for (const entry of scheduleData.entries) {
      if (entry.dayOfWeek && entry.dayOfWeek.toUpperCase() === currentDayOfWeek) {
        // Compare time strings (HH:MM format)
        if (entry.startTime && entry.endTime) {
          if (currentTime >= entry.startTime && currentTime < entry.endTime) {
            return true;
          }
        }
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking pod availability:', error);
    return false;
  }
};
